import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { LegOptionContext } from 'Modules/MultiStopWizard/Components/Leg/Contexts/LegOptionContext';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';

/**
 * Trip notes on a sailing in the ticket list.
 *
 * <p><b>One control, both states.</b> A chosen sailing used to swap the marker for a disclosure
 * strip across the foot of the card, which meant the notes were read one way before choosing and
 * another way after — and the strip's own expand/collapse was a second thing to press before any
 * note could be read at all. The pill is now the same in the list and on the chosen card, and the
 * notes open beside it.</p>
 *
 * <p>Hover on a desktop, tap on a phone, via `openOnHover`. `floating` because the pill lives in
 * the capacity row, inside the fare table, the chosen card's shell and — on a phone — the sailing
 * card, each of which clips to a rounded box; an absolutely-positioned panel is cut off by all
 * three.</p>
 *
 * <p>Note what opening-on-demand costs, since it is a deliberate trade: a customer can pay without
 * ever reading a note. If one has to be <i>acknowledged</i> rather than merely available, this is
 * not the control for it — that belongs on the cart or payment step, where refusing to continue is
 * possible.</p>
 */
export default function TripOptionNotes() {
	const context = React.useContext(LegOptionContext);
	const intl = useIntl();

	if (!context) {
		console.error('LegOptionContext is missing in TripOptionNotes');
		return null;
	}

	// Server-filtered for this caller; a trip with no readable note has an empty list.
	const notes = context.trip?.notes ?? [];

	if (notes.length === 0) {
		return null;
	}

	return (
		<Popover
			floating
			openOnHover
			// The card behind this is itself a button — see `stopPropagation`.
			stopPropagation
			containerClassName="trip-notes"
			buttonClassName="trip-notes__trigger"
			popoverClassName="trip-notes__panel"
			buttonTestId="trip-option-notes-trigger"
			buttonContent={
				<span
					className="trip-option__notes-chip"
					aria-label={intl.formatMessage(
						{ id: 'trip-option__trip-notes-count__label', defaultMessage: '{count} trip notes' },
						{ count: notes.length },
					)}
				>
					<Icon name="info" useMaterialIcons />
					<FormattedMessage id="leg-option__trip-notes__label" defaultMessage="Trip notes" />
					{notes.length > 1 && <span className="trip-option__notes-count">{notes.length}</span>}
				</span>
			}
			popoverContent={() => (
				<ul className="trip-option__notes-list" data-test-id="trip-option-notes-panel">
					{notes.map(note => (
						<li key={note.id} className="trip-option__notes-row">
							<span className="trip-option__notes-bullet" aria-hidden="true" />
							<span>{note.content}</span>
						</li>
					))}
				</ul>
			)}
		/>
	);
}
